/* 
 * React component to overlay the team calendar to select to apply rota time to
 * a person's diary.
 */

import React, {useState, useEffect}
                             from 'react'

import {TwoTimeEdit}                    from '../../lib/iprs-react-library/src/index.js';
import {OKCancel}                       from '../../lib/iprs-react-library/src/index.js';
import AppTypeSelector                  from '../AppointmentType/AppTypeSelect';
import TemplateApply                    from '../Template/TemplateApply'
import userLoggedInHasRole              from '../Util/userLoggedInHasRole';

import {ROTA_TYPE_AVAIABLE}             from '../../lib/iprs-react-library/src/index.js';

const RotaOverlay = ({api, selectInfo, done, calendarApi, setError, state}) => {

    const {start, end,
        resource: { id: rPartyID} = {},
        extendedProps: {apiEvent: {
            RotaID: rotaID                          = 0,
            PartyID: ePartyID                        = 0,
            AppointmentTypes:   PreSelectedAppTypes = []
        } = {} } = {} } = selectInfo

    // extract appointment types from state
    const { appType: { appointmentTypes } = {} }    = state

    const [templateApply, setTemplateApply]         = useState(false);

    const hasDiaryRotaDeletePerm = userLoggedInHasRole(api, 'Diary Rota Delete');

    /* comes from internal event or api depending whether we are creating a new
     * rota entry */
    const partyID = rPartyID? rPartyID: ePartyID

    /* The group of the team calendar being viewed, if any. The picker follows
     * this group's exclusions (business rule: managers only see the types
     * allowed for that team's group). */
    const groupID = state?.team?.teamGroupLookup?.[state?.team?.teamID]?.GroupID

    const handleDeleteClick = e => {
        e.preventDefault()

        api.deleteRota( rotaID )
            .then( r => done() )
            .catch( err => setError( err.message ) )

    }
    
    const handleApplyTemplate = e => {
        
        e.preventDefault();
        
        setTemplateApply( !templateApply );
    }
    
    return (<>
        <div className='wrapper'>
            {/* Flipped: users who hold the Diary Rota Delete role are the ones NOT allowed to delete a rota */}
            {rotaID? <button    onClick={handleDeleteClick} disabled={hasDiaryRotaDeletePerm} >Delete</button>: ''}
            {!rotaID? <button   onClick={handleApplyTemplate} >{templateApply? 'Rota': 'Apply Template'}</button>: null }
        </div>
        
        
        {templateApply?
            <TemplateApply  calendarApi={calendarApi} partyID={partyID} 
                            start={start} api={api} done={done} setError={setError}
                            rotaID={rotaID} state={state} />
        :
            /* FullCalendar gives a zero-length event no end, so fall back to
             * the start to let the end time be corrected */
            <Availability   PreSelectedAppTypes={PreSelectedAppTypes} start={start}
                            end={end || start}  appointmentTypes={appointmentTypes}
                            api={api} partyID={partyID} rotaID={rotaID} groupID={groupID}
                            calendarApi={calendarApi} done={done} setError={setError} />
            }
        
    </>)
}

const Availability = ({     PreSelectedAppTypes, start, end, appointmentTypes, 
                            rotaID, api, partyID, groupID, calendarApi, done, setError}) => {

    
    const [selectedAppTypes, setSelectedAppTypes]   = useState(PreSelectedAppTypes)
    
    /* only ok if you've selected at least on app type.
     * Use True all the time now as people use availablilty for nothing, to mark
     * working hours
     */
    const okEnabled = true; //0 !== selectedAppTypes.length;
    
    const [startTime, setStartTime]                 = useState(start)
    const [endTime,   setEndTime]                   = useState(end)

    /* The rota time is saved before its appointment types, so if the API
     * rejects the types (e.g. one excluded for all of the clinician's team
     * groups) a new rota already exists. Remember its ID so that pressing OK
     * again updates it rather than creating a duplicate. */
    const [savedRotaID, setSavedRotaID]             = useState(rotaID)

    /* Appointment types excluded for the viewed team's group. They're left
     * out of the picker so users don't pick one that team doesn't offer.
     * Ungrouped teams (no groupID) see every type. */
    const [excludedAppTypeIDs, setExcludedAppTypeIDs] = useState([])

    useEffect(() => {
        if (!groupID) { return; }

        api.transact('TeamGroupAppointmentTypeMatrix', {})
            .then(r => setExcludedAppTypeIDs((r.apiResult.AppointmentTypes || [])
                .filter(at => at.ExcludedGroupIDs?.includes(groupID))
                .map(at => at.AppointmentTypeID)))
            /* Fall back to the full list rather than block rota editing */
            .catch(e => console.log('Error loading team group appointment types', e))
    }, [api, groupID])

    /* Types already on the rota stay listed, so editing availability created
     * before a type was excluded still shows (and keeps) it */
    const offeredAppTypes = (appointmentTypes || []).filter(at =>
        !excludedAppTypeIDs.includes(at.AppointmentTypeID) ||
        PreSelectedAppTypes.includes(at.AppointmentTypeID))

    const saveRota = e => {

        e.preventDefault()

        setError( null )

        let createSave
        if (savedRotaID){
            createSave = api.updateRota(savedRotaID, startTime,
                                                    endTime,
                                                    '', // actual rota has no desc
                                                    partyID,
                                                    ROTA_TYPE_AVAIABLE,
                                                    true)
        }else{
            createSave = api.createRota(partyID,    startTime, 
                                                    endTime, 
                                                    '', // actual rota has no desc
                                                    ROTA_TYPE_AVAIABLE,
                                                    true)
        }
        
        createSave.then ( response => response.apiResult )
        .then( apiResult => {
            setSavedRotaID( apiResult.RotaID )
            return api.saveRotaAppointmentType( apiResult.RotaID, selectedAppTypes )
        } )
        .then( apiResult => {

            /* hack to get REACT to re-render*/
            //doRerender( rerender+1 )
            if(calendarApi.unselect){
                calendarApi.unselect() // clear date selection
                calendarApi.render()
            }

            done()
        })
        .catch(e => {
            console.log(e)
            if(calendarApi.unselect){
                calendarApi.unselect() // clear date selection
            }

            /* The rota time may have saved even though its appointment types
             * didn't, so reload the events to show it straight away rather
             * than after the next refresh */
            calendarApi.current?.getApi?.()?.refetchEvents()

            setError( e.apiText || e.message || 'Failed to save availability' )
        })
    }


    return <>
        <h2>Available</h2>
        <TwoTimeEdit start={startTime} end={endTime} onStartChange={setStartTime} 
            onEndChange={setEndTime} startLabel='From' endLabel='To' />
        
        <h2>For Appointment Types</h2>
        <AppTypeSelector appointmentTypes={offeredAppTypes}
            selectedAppTypes={selectedAppTypes} 
            setSelectedAppTypes={setSelectedAppTypes} />
        
        <p></p>
        <OKCancel onOK={saveRota} onCancel={done} enabled={okEnabled} />
       
    </>
}



export default RotaOverlay

